import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Avatar, Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Fragment, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { StatusFooter } from '@/components/StatusFooter';
import { visibleNavItems } from '@/constants/navItems';
import { useAuth } from '@/contexts/AuthContext';
import { useStatusPoll } from '@/hooks/useStatusPoll';
import { APP_VERSION } from '@/utils/version';

export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Optional callback fired whenever a nav item or the logo link is tapped.
   *  Used by the mobile Drawer to auto-close on navigation. */
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onToggle, onNavigate }: SidebarProps): ReactElement {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const status = useStatusPoll();

  const width = collapsed ? 72 : 240;
  const monogram = (user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase();
  const items = visibleNavItems(user?.email);

  return (
    <Box
      component="aside"
      sx={{
        width,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        backgroundColor: 'background.deeper',
        borderRight: '1px solid',
        borderColor: 'rgba(217, 119, 87, 0.18)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 220ms ease',
        zIndex: 10,
      }}
      aria-label="Primary navigation"
    >
      {/* Logo row */}
      <Box
        sx={{
          px: collapsed ? 0 : 2,
          pt: 2,
          pb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 1,
          position: 'relative',
        }}
      >
        <Box
          component={RouterLink}
          to="/insights"
          onClick={onNavigate}
          sx={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          {collapsed ? (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                fontSize: 18,
                color: 'primary.main',
                textShadow: '0 0 6px rgba(217, 119, 87, 0.55)',
              }}
            >
              s
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontWeight: 700,
                  fontSize: 14,
                  color: 'primary.main',
                  textShadow: '0 0 6px rgba(217, 119, 87, 0.55)',
                  letterSpacing: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                sherlock
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 10,
                  color: 'text.faintest',
                  fontWeight: 500,
                }}
              >
                v{APP_VERSION}
              </Typography>
            </>
          )}
        </Box>

        {!collapsed && (
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label="Collapse sidebar"
            sx={{ color: 'text.faintest' }}
          >
            <MenuOpenIcon fontSize="small" />
          </IconButton>
        )}
        {collapsed && (
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label="Expand sidebar"
            sx={{
              color: 'text.faintest',
              position: 'absolute',
              top: 12,
              right: -14,
              backgroundColor: 'background.deeper',
              border: 1,
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'background.paper' },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Nav */}
      <Stack component="nav" sx={{ p: collapsed ? 0.5 : 1, gap: 0.25 }}>
        {items.map((item, idx) => {
          const active = pathname === item.to || pathname.startsWith(item.to + '/');
          // Render a section boundary whenever the group changes:
          //  - expanded + a named group → a muted uppercase header,
          //  - expanded + an UNGROUPED item (e.g. docs) after a group → a thin
          //    divider so it isn't read as part of the prior section,
          //  - collapsed → a thin divider between every group.
          const groupChanged = item.group !== items[idx - 1]?.group;
          let groupHeader: ReactNode = null;
          if (!collapsed && groupChanged && item.group) {
            groupHeader = (
              <Typography
                key={`hdr-${item.group}`}
                sx={{
                  px: 1.5,
                  pt: idx === 0 ? 0.25 : 1,
                  pb: 0.5,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  color: 'text.faintest',
                }}
              >
                {item.group}
              </Typography>
            );
          } else if (groupChanged && idx > 0) {
            groupHeader = <Divider key={`div-${idx}`} sx={{ my: 0.5, borderColor: 'divider' }} />;
          }
          const navContent: ReactNode = collapsed ? (
            <Tooltip title={item.label} placement="right">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 36,
                  borderRadius: 1,
                  borderLeft: '2px solid',
                  borderLeftColor: active ? 'primary.main' : 'transparent',
                  backgroundColor: active ? 'primary.soft' : 'transparent',
                  color: active ? 'primary.main' : 'text.disabled',
                  textShadow: active ? '0 0 6px rgba(217, 119, 87, 0.55)' : 'none',
                  fontFamily: '"JetBrains Mono", monospace',
                  fontWeight: 700,
                  fontSize: 13,
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  },
                }}
              >
                {active ? '▸' : '·'}
              </Box>
            </Tooltip>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                borderLeft: '2px solid',
                borderLeftColor: active ? 'primary.main' : 'transparent',
                backgroundColor: active ? 'primary.soft' : 'transparent',
                color: active ? 'primary.main' : 'text.disabled',
                textShadow: active ? '0 0 6px rgba(217, 119, 87, 0.55)' : 'none',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 13,
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: active ? 'primary.soft' : 'rgba(255, 255, 255, 0.02)',
                },
              }}
            >
              <Box sx={{ minWidth: 14, fontWeight: 700, opacity: 0.85 }}>{active ? '▸' : '·'}</Box>
              <Box sx={{ flex: 1, ml: 1 }}>{item.label}</Box>
            </Stack>
          );

          return (
            <Fragment key={item.to}>
              {groupHeader}
              <Box
                component={RouterLink}
                to={item.to}
                aria-current={active ? 'page' : undefined}
                onClick={onNavigate}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                {navContent}
              </Box>
            </Fragment>
          );
        })}

        {!collapsed && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{
              mt: 0.5,
              px: 1.5,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 10,
              color: 'text.faintest',
            }}
          >
            <Box
              component="span"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 0.5,
                px: 0.6,
                py: 0.05,
              }}
            >
              ⇧
            </Box>
            <Box
              component="span"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 0.5,
                px: 0.6,
                py: 0.05,
              }}
            >
              ↑
            </Box>
            <Box
              component="span"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 0.5,
                px: 0.6,
                py: 0.05,
              }}
            >
              ↓
            </Box>
            <Box component="span" sx={{ ml: 0.5 }}>
              navigate
            </Box>
          </Stack>
        )}
      </Stack>

      <Box sx={{ flex: 1 }} />

      <StatusFooter status={status} variant={collapsed ? 'collapsed' : 'expanded'} />

      {/* User row */}
      {user && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            p: collapsed ? 1 : 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Avatar
            src={user.picture_url}
            alt={user.name ?? user.email}
            sx={{
              width: 22,
              height: 22,
              borderRadius: 0.5,
              backgroundColor: 'primary.soft',
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'primary.strong',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {monogram}
          </Avatar>
          {!collapsed && (
            <>
              <Box sx={{ flex: 1, lineHeight: 1.3 }}>
                <Box sx={{ color: 'text.primary', fontSize: 11 }}>
                  {user.name?.split(/[\s@]/)[0] ?? user.email.split('@')[0]}
                  <Box component="span" sx={{ color: 'text.faintest' }}>
                    @{user.email.split('@')[1] ?? 'detect'}
                  </Box>
                </Box>
                <Box sx={{ color: 'text.faintest', fontSize: 9 }}>eng · admin</Box>
              </Box>
              <Tooltip title="Sign out">
                <IconButton
                  size="small"
                  onClick={signOut}
                  aria-label="Sign out"
                  sx={{
                    color: 'text.faintest',
                    '&:hover': { color: 'primary.main', backgroundColor: 'primary.soft' },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
